import React, { Fragment, MouseEventHandler } from 'react'
import Tooltip from '@mui/material/Tooltip'
import MaskedInput from 'react-text-mask'
import map from 'lodash/map'
import find from 'lodash/find'
import classNames from 'classnames'
import { Box, ClickAwayListener, Popper } from '@mui/material'
import { operationIcons } from './DraggableOperation'
import { LocalizationContext } from '../../shared/contexts'
import Icon from '../../shared/icon'
import { CookingMoveMode, UnloadMoveMode } from '../types'
import { t } from '../../../i18n'
import PlusMinusWrapper from '../../shared/plusMinusWrapper'
import DecimalInput from '../../shared/decimalInput'
import { weightStep, weightUnit } from '../../shared/weightScale'

type Product = {
  id: number,
  file_url: string,
  name: string,
  is_modifier: boolean,
  recipe_configurable: boolean,
  is_critical: boolean
}

interface RecipeTaskProduct {
  product: Product,
  amount: number,
}

const twoDigits = (value) => {
  return (value <= 9) ? `0${value}` : value.toString()
}

const formattedTime = (totalSeconds) => {
  const minutes = twoDigits(Math.floor(totalSeconds / 60))
  const seconds = twoDigits(totalSeconds % 60)

  return `${minutes}:${seconds}`
}

const timeStrToSeconds = (value) => {
  const strTime = value.replaceAll('_', '0') // replace input mask characters
  const [ minutes, seconds ] = strTime.split(':').map(v => parseInt(v))

  return minutes * 60 + seconds
}

interface TaskProps {
  operation: string,
  ungroupTask: Function,
  removeTask: MouseEventHandler,
  removeProductFromTask: MouseEventHandler,
  taskIndex: number,
  updateTaskField: Function,
  updateTaskParameter: Function,
  combineTargetFor: any,
  passedRef: string,
  draggableProps: any,
  dragHandleProps: any,
  isDragging: boolean,
  transform: string,
  stepName: string,
  cooking_move_mode_id: number,
  unload_move_mode_id: number,
  cook_time: number,
  main_stove_level: number,
  aux_stove_level: number,
  updateProductConfigurable: Function,
  groupNumber: number,
  readOnly?: boolean
}

interface AddProductTaskProps extends TaskProps {
  updateTaskProductParameter: Function,
  recipe_task_products: RecipeTaskProduct[],
  updateProductCritical: Function,
}

interface OperationTaskProps extends TaskProps {
  cookingModes: CookingMoveMode[],
  unloadModes: UnloadMoveMode[],
}

const scope = 'recipes'

// The stepper is quoted in grams and the imperial one derived, so the two scales
// move by the same weight per click.
const AMOUNT_STEP_GRAMS = 10

// recipes/_recipe_task.json.jbuilder renders the amount with precision: 2, and
// WeightConverter.scale already rounds the metric branch to whole grams. Capping
// ounces tighter than the server renders would silently round a stored 3.53 down
// on blur.
const amountDecimals = (weightScale: string | undefined) => (weightScale === 'metric' ? 0 : 2)

const AddProductsTask: React.FC<AddProductTaskProps> = ({
  operation, recipe_task_products, ungroupTask, removeTask, removeProductFromTask, taskIndex, updateTaskProductParameter,
  updateTaskParameter, combineTargetFor, passedRef, draggableProps, dragHandleProps,
  transform, stepName, cook_time, updateProductConfigurable, updateProductCritical, groupNumber, readOnly
}) => {
  const isBoilerTask = (stepName === 'boiler_tasks')
  const validCombinableTarget = (combineTargetFor && operation === 'add_product')
  const multipleProducts = (recipe_task_products && recipe_task_products.length > 1)
  const classes = classNames(
    'task', {
      'combinable-target': validCombinableTarget,
      'grouped': multipleProducts
    }
  )


  return (
    <div {...draggableProps} {...dragHandleProps}
      className={classes}
      ref={passedRef}
      style={{ ...draggableProps.style, transform }}
    >
      <div className='task-items-wrapper'>
        {multipleProducts &&
          <div className="task-item group-info">
            <div className="group-name">
              <span className="task-name">Group {groupNumber}</span>
              <Tooltip title='Ungroup' placement='top' className='task-ungroup-btn' arrow>
                <i
                  className='bi bi-box-arrow-down-right'
                  onClick={() => ungroupTask(stepName, taskIndex, null, true)}
                />
              </Tooltip>
            </div>
            {/* {isBoilerTask && boilerTaskTimer()} */}
            {multipleProducts && <span className='task-remove-button group-remove' onClick={removeTask}><Icon name="deleteIcon"/></span>}
          </div>
        }
        <LocalizationContext.Consumer>
          {({ weight_scale }) =>
            map(recipe_task_products, ({ amount, product, batch_recipe }, productIndex) => {
              const { name, file_url, recipe_configurable, is_modifier } = product ?? batch_recipe
              const is_critical = recipe_configurable?.is_critical !== false

              return (
                <div className='task-item' key={productIndex}>
                  <img className='task-image' src={file_url} alt={name} />
                  <span className='task-name'>{name}</span>
                  {multipleProducts &&
                    <Tooltip title='Ungroup' placement='top' className='task-ungroup-btn' arrow>
                      <i
                        className='bi bi-box-arrow-down-right'
                        onClick={() => ungroupTask(stepName, taskIndex, productIndex)}
                      />
                    </Tooltip>
                  }
                  <PlusMinusWrapper
                    name='amount'
                    step={weightStep(AMOUNT_STEP_GRAMS, weight_scale)}
                    value={amount}
                    onChange={(name, value) => updateTaskProductParameter(stepName, taskIndex, productIndex, name, value)}
                  >
                    <Tooltip
                      title={t('recipe_products.amount', { scope })}
                      placement='top'
                      arrow
                    >
                      <div className='subwrapper'>
                        <DecimalInput
                          className={weight_scale === 'metric' ? 'with-unit-offset' : 'with-unit-offset-oz-kg'}
                          name='amount'
                          value={amount}
                          decimals={amountDecimals(weight_scale)}
                          readOnly={readOnly}
                          onChange={(name, value) => updateTaskProductParameter(stepName, taskIndex, productIndex, name, value)}
                        />
                        <span className='input-unit'>{weightUnit(weight_scale)}</span>
                      </div>
                    </Tooltip>
                  </PlusMinusWrapper>
                  {/* {!multipleProducts && isBoilerTask && !boilerTaskTimerRendered && boilerTaskTimer()} */}
                  <Tooltip
                    title={is_modifier ? t('recipe_products.lock', { scope }): t('recipe_products.lock_disabled', { scope })}
                    placement='top'
                    arrow>
                    <div className='task-availability-icon'>
                      {!is_modifier && <Icon name='locked'/>}
                      {is_modifier && (recipe_configurable?.min_value === 0 ?
                        <Icon name='unlocked' onClick={() => updateProductConfigurable(stepName, taskIndex, productIndex, 1)} /> :
                        <Icon name='locked' onClick={() => updateProductConfigurable(stepName, taskIndex, productIndex, 0)} />)
                      }
                    </div>
                  </Tooltip>
                  <Tooltip
                    title={is_critical ? t('recipe_products.critical_ingredient', { scope }) : t('recipe_products.non_critical_ingredient', { scope })}
                    placement='top'
                    arrow>
                    <div className='task-critical-icon'>
                      <i
                        className={`fa fa-exclamation-circle cursor-pointer ${
                          is_critical ? 'text-danger' : 'text-warning'
                        }`}
                        onClick={() => updateProductCritical(stepName, taskIndex, productIndex, !is_critical)}
                      />
                    </div>
                  </Tooltip>
                  <span
                    className='task-remove-button'
                    onClick={(e) => multipleProducts ? removeProductFromTask(productIndex) : removeTask(e)}
                  >
                    <Icon name='deleteIcon' />
                  </span>
                </div>
              )
            })
          }
        </LocalizationContext.Consumer>
      </div>
    </div>
  )
}

const OperationTask: React.FC<OperationTaskProps> = ({
  operation, passedRef, draggableProps, dragHandleProps, removeTask, updateTaskParameter,
  updateTaskField, taskIndex, cooking_move_mode_id, unload_move_mode_id, cook_time,
  main_stove_level, aux_stove_level, cookingModes, unloadModes, transform, stepName
}) => {
  const renderParameters = () => {
    const parameterProps = {
      cookTime: cook_time,
      mainStoveLevel: main_stove_level,
      auxStoveLevel: aux_stove_level,
      updateTaskField,
      updateTaskParameter,
      taskIndex,
      stepName
    }

    switch (operation) {
      case 'set_stove_level':
        return <SetStoveLevelParameters {...parameterProps}
               />
      case 'set_cooking_mode':
        return <SetCookingModeParameters {...parameterProps}
                 cookingModes={cookingModes}
                 cooking_move_mode_id={cooking_move_mode_id}
               />
      case 'unload_boiler':
        return <SetUnloadModeParameters {...parameterProps}
                 unloadModes={unloadModes}
                 unload_move_mode_id={unload_move_mode_id}
               />
      default: null
    }
  }

  return (
    <div {...draggableProps} {...dragHandleProps}
      className='task boiler-task'
      ref={passedRef}
      style={{ ...draggableProps.style, transform }}
    >
      <div className='task-items-wrapper'>
        <div className='task-item'>
          <Icon className='task-image' name={operationIcons[operation]} color='#fff'/>
          <span className='task-name'>{t(`recipe_tasks.operations.${operation}`, { scope })}</span>
        </div>
      </div>
      <div className='task-parameters'>
        {renderParameters()}
      </div>
      <span className='task-remove-button' onClick={removeTask}>&times;</span>
    </div>
  )
}

const SetStoveLevelParameters = ({
  cookTime, mainStoveLevel, auxStoveLevel, updateTaskParameter, taskIndex, stepName
}) => {

  const validStoveLevel = (value) => {
    return (parseInt(value) <= 7 && parseInt(value) >= 0)
  }

  return <Fragment>
    <PlusMinusWrapper
      className='task-parameter ms-2'
      name='main_stove_level'
      step={1}
      value={mainStoveLevel || 0}
      onChange={(name, value) => {
        if(validStoveLevel(value)) {
          updateTaskParameter(stepName, taskIndex, name, value)
        }
      }}
    >
      <Tooltip
        title={t('recipe_tasks.set_heat', { scope })}
        placement='top'
        arrow
      >
        <div className='subwrapper'>
          <input
            type='number'
            name='main_stove_level'
            value={mainStoveLevel || 0}
            onChange={({ target: { name, value } }) => {
              if(validStoveLevel(value)) {
                updateTaskParameter(stepName, taskIndex, name, value)
              }
            }
            }
          />
        </div>
      </Tooltip>
    </PlusMinusWrapper>
    <PlusMinusWrapper
      className='task-parameter ms-2'
      name='aux_stove_level'
      step={1}
      value={auxStoveLevel || 1}
      onChange={(name, value) => {
        if(validStoveLevel(value)) {
          updateTaskParameter(stepName, taskIndex, name, value)
        }
      }}
    >
      <Tooltip
          title={t('recipe_tasks.keep_heat', { scope })}
          placement='top'
          arrow
      >
        <div className='subwrapper'>
          <input
            type='text'
            name='aux_stove_level'
            value={auxStoveLevel || 1}
            maxLength={1}
            onChange={({ target: { name, value } }) => {
              if(validStoveLevel(value)) {
                updateTaskParameter(stepName, taskIndex, name, value)
              }
            }}
          />
        </div>
      </Tooltip>
    </PlusMinusWrapper>
  </Fragment>
}

const SetCookingModeParameters = ({
  cooking_move_mode_id, updateTaskField, taskIndex, cookingModes, stepName, cookTime, updateTaskParameter
}) => {

  const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(null)
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setAnchorElement(anchorElement ? null : anchorRef.current);
  };

  const closeDropdown = () => {
    setAnchorElement(null)
  }

  const open = Boolean(anchorElement);
  const id = open ? 'simple-popper' : undefined;

  const selectMode = (modeId) => {
    updateTaskField(stepName, taskIndex, 'cooking_move_mode_id', modeId)
    closeDropdown()
  }

  const timeMask = [/[0-5]/, /[0-9]/, ':', /[0-5]/, /[0-9]/];

  const boilerTaskTimer = () => {
    return (
      <div className='task-parameters'>
        <PlusMinusWrapper
          className='ms-2'
          name='cook_time'
          step={5}
          value={cookTime}
          onChange={(name, value) => updateTaskParameter(stepName, taskIndex, name, value)}
        >
          <div className='subwrapper'>
            <Tooltip
              title={t('recipe_products.cook_time', { scope })}
              placement='top'
              arrow
            >
              <div>
                <MaskedInput
                  mask={timeMask}
                  value={formattedTime(cookTime)}
                  name='cook_time'
                  onChange={({ target: { name, value } }) =>
                    updateTaskParameter(stepName, taskIndex, name, timeStrToSeconds(value))
                  }
                  render={(ref, props) => (
                    <input ref={ref} {...props} />
                  )}
                />
              </div>
            </Tooltip>
          </div>
        </PlusMinusWrapper>
      </div>
    )
  }

  return (
    <Fragment>
      <Tooltip
        title='Set cooking mode'
        placement='top'
        className='task-parameter'
        arrow
      >
        <div>
          <ClickAwayListener onClickAway={closeDropdown}>
            <div className="cooking-mode-select-dropdown">
              <div className="select-dropdown-placeholder" aria-describedby={id} onClick={handleClick} onBlur={handleClick} ref={anchorRef}>
                <span className={`label ${cooking_move_mode_id ? '' : 'placeholder'}`}>
                  {cooking_move_mode_id
                    ? find(cookingModes, opt => opt.id === cooking_move_mode_id)?.name
                    : t('recipe_tasks.cooking_mode.select_placeholder', { scope })
                  }
                </span>
                <Icon name={open ? 'arrowUp' : 'arrowDown'}/>
              </div>
              <Popper
                style={{zIndex: '2'}}
                placement="bottom"
                id={id} open={open}
                anchorEl={anchorElement}
                disablePortal={false}
              >
                <Box
                  style={{zIndex: '2'}}
                  sx={{ bgcolor: 'background.paper' }}
                  className="dropdown-content move-mode-dropdown"
                >
                  {map(cookingModes, cookingMode =>
                    <ModeLabel
                      key={cookingMode.id}
                      mode={cookingMode}
                      selectMode={() => selectMode(cookingMode.id)}
                    />
                  )}
                </Box>
              </Popper>
            </div>
        </ClickAwayListener>
      </div>
    </Tooltip>
    {boilerTaskTimer()}
  </Fragment>
  )
}

const ModeLabel = ({ mode, selectMode }) => {
  return(
    <div className="mode-label" onClick={selectMode}>
      <span className="mode-name">{mode.name}</span>
      <span className="mode-description">{mode.description}</span>
    </div>
  )
}

const SetUnloadModeParameters = ({
  unload_move_mode_id, updateTaskField, taskIndex, unloadModes, stepName
}) => {

  const [anchorElement, setAnchorElement] = React.useState<null | HTMLElement>(null)
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setAnchorElement(anchorElement ? null : anchorRef.current);
  };

  const closeDropdown = () => {
    setAnchorElement(null)
  }

  const open = Boolean(anchorElement);
  const id = open ? 'simple-popper' : undefined;

  const selectMode = (unloadModeId) => {
    updateTaskField(stepName, taskIndex, 'unload_move_mode_id', unloadModeId)
    closeDropdown()
  }

  return (
    <Tooltip
      title='Set unload mode'
      placement='top'
      className='task-parameter'
      arrow
    >
      <div>
        <ClickAwayListener onClickAway={closeDropdown}>
          <div className="unload-mode-select-dropdown">
            <div className="select-dropdown-placeholder" aria-describedby={id} onClick={handleClick} onBlur={handleClick} ref={anchorRef}>
              <span className={`label ${unload_move_mode_id ? '' : 'placeholder'}`}>
                {unload_move_mode_id
                  ? find(unloadModes, opt => opt.id === unload_move_mode_id)?.name
                  : t('recipe_tasks.unload_mode.select_placeholder', { scope })
                }
              </span>
              <Icon name={open ? 'arrowUp' : 'arrowDown'}/>
            </div>
            <Popper
              style={{zIndex: '2'}}
              placement="bottom"
              id={id} open={open}
              anchorEl={anchorElement}
              disablePortal={false}
            >
              <Box
                style={{zIndex: '2'}}
                sx={{ bgcolor: 'background.paper' }}
                className="dropdown-content move-mode-dropdown"
              >
                {map(unloadModes, unloadMode =>
                  <ModeLabel
                    key={unloadMode.id}
                    mode={unloadMode}
                    selectMode={() => selectMode(unloadMode.id)}
                  />
                )}
              </Box>
            </Popper>
          </div>
      </ClickAwayListener>
    </div>
  </Tooltip>
  )
}

const Task = ({
  recipe_task_products, updateTaskField, updateTaskProductParameter,
  cookingModes, unloadModes, groupNumber, updateProductCritical, ...props
}: any) => {
  let transform = props.draggableProps.style.transform

  // handle case when dragging operation - lower margin than default
  if(!!transform && transform.replace(/\s/g,'') == 'translate(0px,80px)') {
      transform = 'translate(0px, 55px)'
  }

  // handle same thing for products - flickering when letting go of dragging item
  if(!!transform && transform.replace(/\s/g,'') == 'translate(0px,50px)') {
    transform = 'translate(0px, 62px)'
  }

  // handle case of dragging operation that is already in tasks
  if(!!transform && transform.replace(/\s/g,'') == 'translate(0px,65px)') {
    transform = 'translate(0px, 60px)'
  }


  const operation = props.operation

  if (operation === 'add_product' || operation === 'add_sauce' || operation === 'add_batch_recipe') {
    return (
      <AddProductsTask {...props as TaskProps}
        operation={operation}
        transform={transform}
        recipe_task_products={recipe_task_products}
        updateTaskProductParameter={updateTaskProductParameter}
        updateProductCritical={updateProductCritical}
        groupNumber={groupNumber}
      />
    )
  } else {
    return (
      <OperationTask {...props as TaskProps}
        operation={operation}
        transform={transform}
        cookingModes={cookingModes}
        unloadModes={unloadModes}
        updateTaskField={updateTaskField}
      />
    )
  }
}

export default Task
